/**
 * OS ROMANEIOS — o papel de cada venda, para reabrir e reenviar.
 *
 * Pedido do gestor em 06/10/2026, depois de despescar o T-2: gravada a
 * despesca, o romaneio sumia junto com o formulário, «e não encontrei ele em
 * canto algum». Precisava dele para reencaminhar ao comprador.
 *
 * Uma lista, da venda mais recente para a mais antiga, e um romaneio aberto
 * de cada vez — o papel é um só na página (ver `RomaneioAberto`). As
 * despescas ainda na fila do aparelho entram no topo, marcadas: o comprador
 * está esperando na beira da represa, e o sinal não.
 *
 * O ROMANEIO REABERTO É O MESMO PAPEL. Ele é refeito do que o banco guardou
 * pela mesma conta que o emitiu, e o teste confere centavo por centavo.
 */

import { useEffect, useState } from 'react'
import { listarRomaneios, romaneiosNaFila, type Perfil, type RomaneioEmitido } from '../acesso/api'
import { romaneioGravado } from '../dominio/romaneio'
import { diaCurto, inteiro, numero } from '../dominio/unidades'
import { RomaneioAberto } from './RomaneioAberto'

export function Romaneios({ perfil }: { perfil: Perfil }) {
  // DINHEIRO SÓ PARA GESTOR E ADM (06/10/2026). O servidor já não manda o
  // preço ao tratador; aqui a tela também não mostra a coluna — nem a das
  // despescas da fila, cujo preço está no próprio aparelho.
  const semValores = perfil !== 'gestor' && perfil !== 'administrador'
  const [doBanco, setDoBanco] = useState<RomaneioEmitido[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState<string | null>(null)

  useEffect(() => {
    listarRomaneios()
      .then((r) => {
        setDoBanco(r)
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
  }, [])

  const todos = [...romaneiosNaFila(), ...doBanco]

  return (
    <section className="bloco">
      <h2>Romaneios</h2>
      <p className="secundario">
        O papel de cada despesca, para imprimir, salvar em PDF ou mandar de novo ao comprador.
      </p>

      {erro && (
        <p className="aviso-erro">
          Os romaneios do banco não carregaram ({erro}).
          {todos.length > 0 ? ' Os deste aparelho estão abaixo.' : ''}
        </p>
      )}
      {carregando && <p className="aviso">Carregando…</p>}
      {!carregando && todos.length === 0 && !erro && (
        <p className="aviso">Nenhuma despesca registrada ainda.</p>
      )}

      <ul className="lista-romaneios">
        {todos.map((r) => {
          const chave = r.chave
          const romaneio = romaneioGravado(r.linhas, r.desconto_umidade)
          const estaAberto = aberto === chave
          return (
            <li key={chave} className={estaAberto ? 'romaneio-item aberto' : 'romaneio-item'}>
              <button
                type="button"
                className="romaneio-linha"
                onClick={() => setAberto(estaAberto ? null : chave)}
                aria-expanded={estaAberto}
              >
                <span className="romaneio-quem">
                  <strong>{r.tanque || '—'}</strong> · {diaCurto(r.data)}
                  {r.ciclo && <span className="secundario"> · {r.ciclo}</span>}
                </span>
                <span className="romaneio-quanto secundario">
                  {romaneio
                    ? `${inteiro(romaneio.peixes)} peixes · ${numero(romaneio.pesadoKg, 1)} kg`
                    : 'sem linhas'}
                  {r.comprador ? ` · ${r.comprador}` : ''}
                </span>
                <span className="romaneio-etiquetas">
                  {r.encerrou ? <span className="etiqueta">encerrou</span> : <span className="etiqueta">parcial</span>}
                  {r.pendente && !r.recusa && <span className="etiqueta etiqueta-fria">na fila</span>}
                  {r.recusa && <span className="etiqueta etiqueta-recusada">recusada</span>}
                </span>
              </button>

              {estaAberto && r.recusa && (
                <p className="aviso-erro romaneio-aberto">
                  O servidor recusou esta despesca ({r.recusa}). O papel abaixo existe, mas a
                  venda não está no banco — veja em «ver o que está esperando».
                </p>
              )}
              {estaAberto && romaneio && (
                <div className="romaneio-aberto">
                  <RomaneioAberto
                    semValores={semValores}
                    romaneio={romaneio}
                    cabecalho={{
                      represa: r.tanque,
                      ciclo: r.ciclo,
                      dia: r.data,
                      comprador: r.comprador ?? '',
                      destino: r.destino ?? '',
                      observacao: r.observacao ?? '',
                    }}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {doBanco.length >= 60 && (
        <p className="secundario">
          Mostrando as 60 despescas mais recentes. As mais antigas continuam no banco.
        </p>
      )}
      {/* UMA DESPESCA PODE APARECER DUAS VEZES por alguns minutos: subiu, mas a
          resposta do servidor não voltou a tempo, e ela segue na fila até o
          próximo Sincronizar. Aceito de propósito — esconder a da fila exigiria
          adivinhar qual linha do banco é ela. */}
    </section>
  )
}
